import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Sparkles, AlertTriangle, ChevronRight } from "lucide-react";
import { ClimateRiskBadge, PermissionStatusBadge } from "../components/Badges";

export default function Provenance() {
  const { data: collections = [], isLoading } = useQuery({
    queryKey: ["provenance-list"],
    queryFn: async () => (await api.get("/collections")).data,
  });

  return (
    <div className="space-y-8" data-testid="provenance-page">
      <header>
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">Provenance Intelligence</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#2C2A28] mt-2">
          Draft intelligence across every collection
        </h1>
        <p className="text-[13.5px] text-[#5C5854] mt-2 max-w-2xl leading-relaxed">
          Pattern-based summaries for each provenance — generated locally as <span className="font-semibold">draft intelligence</span>.
          They surface gaps, risks, and recommendations to be verified with cultural and scientific authority.
        </p>
      </header>

      <Card className="sk-ai-surface p-5 rounded-md text-[12.5px] text-[#5C5854] leading-relaxed" data-testid="provenance-disclaimer">
        <div className="flex items-start gap-3">
          <Sparkles className="w-4 h-4 mt-0.5 text-[#8B5A2B]" strokeWidth={1.8} />
          <div>
            <strong className="text-[#2C2A28]">Draft intelligence</strong> · This engine uses rule-based heuristics so the
            system can run without sending cultural data to external models. AI use is opt-in per collection, governed by
            Traditional Owner permissions. A future release may connect to an LLM with explicit AI-analysis consent.
          </div>
        </div>
      </Card>

      {isLoading && <div className="text-[12px] text-[#8A857D] tracking-[0.2em] uppercase">Loading…</div>}

      <div className="grid lg:grid-cols-2 gap-5">
        {collections.map((c) => {
          const ai = c.ai_insights || {};
          return (
            <Link key={c.id} to={`/collections/${c.id}`} className="group" data-testid={`prov-card-${c.id}`}>
              <Card className="p-6 border-[#E6E2D8] shadow-sm bg-white hover:shadow-md transition-all duration-200">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-mono-data text-[11px] text-[#8A857D]">{c.collection_id}</div>
                    <div className="font-serif-display italic text-[18px] text-[#2C2A28] mt-1">{c.species_scientific_name || "Unidentified species"}</div>
                    <div className="text-[12.5px] text-[#5C5854]">{c.species_common_name}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#8A857D]">Confidence</div>
                    <div className="font-mono-data text-2xl text-[#2C2A28]">{ai.confidence_score ?? "—"}%</div>
                  </div>
                </div>

                <p className="text-[13.5px] leading-relaxed text-[#2C2A28] mt-4">
                  {ai.provenance_summary || "No summary generated yet."}
                </p>

                <div className="flex flex-wrap gap-2 mt-4">
                  <PermissionStatusBadge status={c.cultural?.cultural_permission_status} />
                  <ClimateRiskBadge score={c.climate?.climate_risk_score} />
                </div>

                {(ai.risk_warnings?.length || 0) > 0 && (
                  <div className="mt-4 flex items-start gap-2 text-[12.5px] text-[#B45B3E]">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{ai.risk_warnings.length} risk warning{ai.risk_warnings.length === 1 ? "" : "s"}</span>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B]">
                  <span>Open profile</span>
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
